import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, DoorOpen, Settings2, Globe, ChevronLeft, ChevronRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { api, errMsg, fmtPts } from "../lib/api";
import { InviteCode } from "../components/InviteCode";
import { Leaderboard } from "../components/Leaderboard";
import { RoundPredictions } from "../components/RoundPredictions";
import { Submissions } from "../components/Submissions";
import { RoundSelect } from "../components/RoundSelect";
import { DeleteTournament } from "../components/DeleteTournament";
import { TournamentSettingsDialog } from "../components/TournamentSettingsDialog";

const RulesStrip = ({ t }) => (
  <div className="flex flex-wrap gap-2 text-xs font-mono" data-testid="tournament-rules">
    <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400">Fissa {t.scoring.fisse} pt · {t.limits.unlimited ? "senza limite" : `max ${t.limits.fisse}`}</span>
    <span className="px-3 py-1 rounded-full bg-blue-500/10 text-blue-400">Doppia {fmtPts(t.scoring.doppie)} pt · {t.limits.unlimited ? "senza limite" : `max ${t.limits.doppie}`}</span>
    <span className="px-3 py-1 rounded-full bg-violet-500/10 text-violet-400">Esatto +{t.scoring.esatto} pt · max {t.limits.esatti}</span>
    <span className="px-3 py-1 rounded-full bg-amber-500/10 text-amber-400">Bonus giornata +{t.scoring.bonus}</span>
  </div>
);

export default function TournamentPage() {
  const { tid } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [rounds, setRounds] = useState(null);
  const [round, setRound] = useState(null);
  const tabsRef = useRef(null);

  const goRound = (delta) => {
    const i = rounds?.findIndex((r) => r.key === round) ?? -1;
    const next = rounds?.[i + delta];
    if (next) setRound(next.key);
    return next;
  };
  const [editable, setEditable] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tab, setTab] = useState("predictions");

  const load = useCallback(() => {
    api.get(`/tournaments/${tid}`).then((r) => setT(r.data)).catch((e) => { toast.error(errMsg(e)); nav("/"); });
    api.get(`/tournaments/${tid}/settings`).then((r) => setEditable(r.data.rules_editable)).catch(() => {});
  }, [tid, nav]);

  const loadRounds = useCallback((initial) => {
    api.get(`/tournaments/${tid}/rounds`).then((r) => {
      setRounds(r.data.rounds);
      if (!initial) return;
      setRound(r.data.current);
      if (r.data.current) api.get(`/tournaments/${tid}/matches`, { params: { round: r.data.current } })
        .then((m) => m.data.some((x) => x.prediction) && setTab("submissions")).catch(() => {});
    }).catch(() => setRounds([]));
  }, [tid]);

  useEffect(() => {
    load();
    loadRounds(true);
  }, [load, loadRounds]);

  const leave = async () => {
    try {
      await api.delete(`/tournaments/${tid}/leave`);
      toast.success("Hai lasciato il torneo");
      nav("/");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/tournaments/${tid}`);
      toast.success("Torneo eliminato");
      nav("/");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!t) return <div className="card-surface h-48 animate-pulse" data-testid="tournament-loading" />;

  return (
    <div className="space-y-3 -mt-2 sm:-mt-9">
      <Link to="/" className="inline-flex items-center gap-2 text-xs text-slate-400 hover:text-white" data-testid="back-to-dashboard"><ArrowLeft size={14} /> Dashboard</Link>
      <section className="space-y-0.5 fade-up">
        <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">{t.competition_name}</p>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight leading-none min-w-0 break-words" data-testid="tournament-name">{t.name}</h1>
        <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
          {t.visibility === "public" ? (
            <span className="h-8 px-3 rounded-full border border-emerald-500/40 text-emerald-400 flex items-center gap-1.5 text-xs" data-testid="public-badge"><Globe size={14} /> Pubblico</span>
          ) : <InviteCode code={t.invite_code} name={t.name} />}
          {!t.is_admin && <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="h-8 px-3 rounded-full border border-[#26334D] text-slate-400 hover:text-red-400 hover:border-red-500/50 flex items-center gap-1.5 text-xs transition-colors" data-testid="leave-tournament-button">
                <DoorOpen size={14} /> <span className="hidden sm:inline">Esci dal torneo</span>
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#121824] border-[#26334D]">
              <AlertDialogHeader>
                <AlertDialogTitle>Uscire dal torneo?</AlertDialogTitle>
                <AlertDialogDescription>I tuoi pronostici verranno cancellati.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="leave-cancel">Annulla</AlertDialogCancel>
                <AlertDialogAction onClick={leave} className="bg-red-500 hover:bg-red-600" data-testid="leave-confirm">Esci</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>}
          {t.is_admin && editable && (
            <button onClick={() => setSettingsOpen(true)} data-testid="edit-tournament-button" className="h-8 px-3 rounded-full border border-[#26334D] text-slate-300 hover:border-emerald-500/60 flex items-center gap-1.5 text-xs transition-colors">
              <Settings2 size={14} /> <span className="hidden sm:inline">Modifica</span>
            </button>
          )}
          {t.is_admin && <DeleteTournament onConfirm={remove} />}
          {t.is_admin && <TournamentSettingsDialog t={t} open={settingsOpen} onOpenChange={setSettingsOpen} onSaved={load} />}
        </div>
        </div>
        <p className="flex items-center gap-2 text-slate-400 text-xs pt-1"><Users size={12} /> {t.members_count} {t.members_count === 1 ? "giocatore" : "giocatori"}</p>
      </section>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "leaderboard") requestAnimationFrame(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })); }}>
        <div ref={tabsRef} className="scroll-mt-20 flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
          <TabsList className="bg-[#121824] border border-[#26334D] h-11">
            <TabsTrigger value="predictions" data-testid="tab-predictions" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-[#0A0D14]">Pronostici</TabsTrigger>
            <TabsTrigger value="leaderboard" data-testid="tab-leaderboard" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-[#0A0D14]">Classifica</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-submissions" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-[#0A0D14]">Consegnati</TabsTrigger>
          </TabsList>
          {rounds && (
            <div className="flex items-center gap-2">
              <button onClick={() => goRound(-1)} data-testid="prev-round-button" title="Giornata precedente" className="h-11 w-11 shrink-0 grid place-items-center rounded-lg border border-[#26334D] bg-[#121824] text-slate-300 hover:text-emerald-400"><ChevronLeft size={18} /></button>
              <div className="flex-1"><RoundSelect rounds={rounds} value={round} onChange={setRound} /></div>
              <button onClick={() => goRound(1)} data-testid="next-round-button" title="Giornata successiva" className="h-11 w-11 shrink-0 grid place-items-center rounded-lg border border-[#26334D] bg-[#121824] text-slate-300 hover:text-emerald-400"><ChevronRight size={18} /></button>
            </div>
          )}
        </div>
        <div data-testid="round-content-area">
        <TabsContent value="predictions" className="mt-4">
          {rounds?.length === 0 && <p className="card-surface p-8 text-slate-400" data-testid="no-rounds">Il calendario della competizione non è ancora disponibile. Si aggiorna automaticamente ogni 15 minuti.</p>}
          {round && <RoundPredictions tid={tid} round={round} info={rounds?.find((r) => r.key === round)} limits={t.limits} onSaved={load} onLive={() => { load(); loadRounds(false); }} />}
        </TabsContent>
        <TabsContent value="leaderboard" className="mt-2">
          <Leaderboard t={t} onChanged={load} />
        </TabsContent>
        <TabsContent value="submissions" className="mt-3">
          {round && <Submissions tid={tid} round={round} />}
        </TabsContent>
        </div>
      </Tabs>
      <section className="pt-6 border-t border-[#26334D] space-y-3" data-testid="tournament-rules-section">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Regole del torneo</p>
        <RulesStrip t={t} />
      </section>
    </div>
  );
}
